import { CheckCircleFilled } from '@ant-design/icons';
import styled from '@emotion/styled';
import { FormikErrors, useFormikContext } from 'formik';
import { HTMLAttributes } from 'react';
import { useTranslate } from '@tolgee/react';
import { Link } from 'react-scroll';
import { ReactScrollLinkProps } from 'react-scroll/modules/components/Link';
import { FV } from '../share';
import { useGetRegionLabel } from 'hooks/business/useRegion';
import { useReactScrollContext } from 'views/Aide/components/ReactScrollContext';

const activeClass = 'linkActive';

export const Container = styled.nav`
  min-width: 180px;
  padding: 16px;
  border-radius: 2px;
  > a {
    display: flex;
    align-items: center;
    position: relative;
    height: 32px;
    padding-right: 24px;
    font-size: 16px;
    color: #515359;
    border-radius: 2px;
    > span {
      width: 16px;
      height: 16px;
      margin: 0 8px;
      border: solid 1.5px #d3d7e0;
      border-radius: 8px;
    }
    > .anticon {
      color: var(--ant-primary-color);
      border: none;
    }
    &::before,
    &::after {
      content: '';
      display: inline-block;
      position: absolute;
      left: 16px;
      width: 1px;
      height: 8px;
      background: #f0f2f5;
    }
    &::before {
      top: 0;
    }
    &::after {
      bottom: 0;
      &:last-of-type {
        display: none;
      }
    }
    &:first-of-type::before {
      display: none;
    }
    &:last-of-type::after {
      display: none;
    }
    &:hover {
      background: #f8f9fa;
      cursor: pointer;
      &::before,
      &::after {
        display: none;
      }
    }
  }
  .${activeClass} {
    color: var(--ant-primary-color);
    background: #f8f9fa;
    &::before,
    &::after {
      display: none;
    }
  }
`;

export const linkProps: Partial<ReactScrollLinkProps> = {
  smooth: true,
  spy: true,
  isDynamic: true,
  activeClass,
  duration: 500,
  delay: 0,
  offset: -74,
};

export interface NavProps<FormValue extends Pick<FV, 'regionExecuteContexts'>>
  extends HTMLAttributes<HTMLElement> {
  supportBasic?: boolean;
  basicValidate: (errors: FormikErrors<FormValue>) => boolean;
}

function Nav<FormValue extends Pick<FV, 'regionExecuteContexts'>>(props: NavProps<FormValue>) {
  const { supportBasic = true, basicValidate, ...otherProps } = props;

  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();

  const {
    errors,
    values: { regionExecuteContexts = [] },
  } = useFormikContext<FormValue>();

  const { containerId, basicId, createRegionContextId } = useReactScrollContext();

  return (
    <Container {...otherProps}>
      {supportBasic && (
        <Link to={basicId} containerId={containerId} {...linkProps}>
          {basicValidate(errors) ? <CheckCircleFilled /> : <span />}
          {t('aide.基础配置')}
        </Link>
      )}
      {regionExecuteContexts.map(({ enable = true, region }, index) => {
        if (!enable) {
          return null;
        }

        const { regionExecuteContexts: { [index]: error } = [] } = errors;

        return (
          <Link
            key={region}
            to={createRegionContextId(region)}
            containerId={containerId}
            {...linkProps}
          >
            {error ? <span /> : <CheckCircleFilled />}
            {getRegionLabel(region)}
          </Link>
        );
      })}
    </Container>
  );
}

export default Nav;
